import api from './client';

// Types
export interface JournalEntry {
  id: string;
  entryNumber: string;
  date: string;
  description: string;
  reference?: string;
  totalDebit: number;
  totalCredit: number;
  status: 'draft' | 'posted' | 'reversed';
  postedBy?: string;
  postedDate?: string;
  reversedBy?: string;
  reversedDate?: string;
  source: 'manual' | 'sales' | 'purchase' | 'payment' | 'system';
  lineItems: JournalEntryLine[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface JournalEntryLine {
  id: string;
  accountId: string;
  accountName: string;
  debitAmount: number;
  creditAmount: number;
  description?: string;
  reference?: string;
}

export interface CreateJournalEntryDto {
  date: string;
  description: string;
  reference?: string;
  source: 'manual' | 'sales' | 'purchase' | 'payment' | 'system';
  lineItems: CreateJournalEntryLineDto[];
  notes?: string;
}

export interface CreateJournalEntryLineDto {
  accountId: string;
  debitAmount: number;
  creditAmount: number;
  description?: string;
  reference?: string;
}

export interface UpdateJournalEntryDto {
  date?: string;
  description?: string;
  reference?: string;
  source?: 'manual' | 'sales' | 'purchase' | 'payment' | 'system';
  lineItems?: CreateJournalEntryLineDto[];
  notes?: string;
}

export interface GetJournalEntriesFilters {
  status?: 'draft' | 'posted' | 'reversed';
  source?: 'manual' | 'sales' | 'purchase' | 'payment' | 'system';
  accountId?: string;
  dateFrom?: string;
  dateTo?: string;
  amountFrom?: number;
  amountTo?: number;
  search?: string;
}

// API functions
export const journalApi = {
  // Get all journal entries
  async getJournalEntries(filters: GetJournalEntriesFilters = {}): Promise<JournalEntry[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `accounting/journal${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<JournalEntry[]>(url);
    return response.data;
  },

  // Get journal entry by ID
  async getJournalEntry(id: string): Promise<JournalEntry> {
    const response = await api.get<JournalEntry>(`accounting/journal/${id}`);
    return response.data;
  },

  // Create journal entry
  async createJournalEntry(data: CreateJournalEntryDto): Promise<JournalEntry> {
    const response = await api.post<JournalEntry>('accounting/journal', data);
    return response.data;
  },

  // Update journal entry (only drafts can be updated)
  async updateJournalEntry(id: string, data: UpdateJournalEntryDto): Promise<JournalEntry> {
    const response = await api.put<JournalEntry>(`accounting/journal/${id}`, data);
    return response.data;
  },

  // Delete journal entry (only drafts can be deleted)
  async deleteJournalEntry(id: string): Promise<void> {
    await api.delete(`accounting/journal/${id}`);
  },

  // Post journal entry (change status from draft to posted)
  async postJournalEntry(id: string): Promise<JournalEntry> {
    const response = await api.post<JournalEntry>(`accounting/journal/${id}/post`);
    return response.data;
  },

  // Reverse journal entry (change status from posted to reversed)
  async reverseJournalEntry(id: string, reason?: string): Promise<JournalEntry> {
    const response = await api.post<JournalEntry>(`accounting/journal/${id}/reverse`, { reason });
    return response.data;
  },

  // Get journal entries by account
  async getJournalEntriesByAccount(accountId: string): Promise<JournalEntry[]> {
    const response = await api.get<JournalEntry[]>(`accounting/journal/account/${accountId}`);
    return response.data;
  },

  // Get next entry number
  async getNextEntryNumber(): Promise<{ entryNumber: string }> {
    const response = await api.get<{ entryNumber: string }>('accounting/journal/next-number');
    return response.data;
  }
};

export default journalApi; 